import { Descriptions, Space, Typography } from 'antd';
import React, { FC } from 'react';
import { BookingDataState } from '../../model';
import { FONT } from '../../styles/color';
import { ACTIVE_PRIVATE_HOSPITAL_CONFIG } from '../../config/privateBooking';

type Props = {
  bookingData: BookingDataState,
};

export const BookingSummaryDetails: FC<Props> = ({ bookingData }) => {
  const {
    healthSummary, medicalSupporting, additionalReport2, additionalReport1,
  } = bookingData.attachments || {};
  const { referrerDetails, gpDetails, clientDetails, referralDetails } = ACTIVE_PRIVATE_HOSPITAL_CONFIG || {};
  const { showPhn = true, title = 'Referrer Details', phnLabelInSummary } = referrerDetails || {};
  const { showFaxAndEmial = true, showGpDetailsPage = true, showReferrerDetailsPage = true } = gpDetails || {};
  const {
    mentalHealthQuestion, clientIdLabel, showClientIdInSummary, showReasonForReferral = true,
    showMobile, showEmail, showAboriginalQuestion,
  } = clientDetails || {};
  const { primaryPresentingProblem, currentMedication, mmhcCentreDetails } = referralDetails || {};

  const titleStyle = { fontSize: '18px', color: FONT };

  return (
    <div style={{ marginBottom: '50px' }}>
      <Typography.Title level={2} style={{ marginBottom: '16px', color: '#5f6369', fontSize: '36px' }}>
        Referral Summary
      </Typography.Title>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Descriptions title={<span style={titleStyle}>Client Details</span>} bordered size="small" column={2}>
          {
            showClientIdInSummary && (
            <Descriptions.Item label={clientIdLabel}>{bookingData.client?.mmhClientId}</Descriptions.Item>
            )
          }
          {bookingData.metadata?.centreName && (
            <Descriptions.Item label={phnLabelInSummary} span={2}>{bookingData.metadata.centreName}</Descriptions.Item>
          )}
          <Descriptions.Item label="First Name">{bookingData.client?.firstName}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{bookingData.client?.lastName}</Descriptions.Item>
          <Descriptions.Item label="Date of Birth">{bookingData.client?.dateOfBirth}</Descriptions.Item>
          {showMobile && (
          <Descriptions.Item label="Mobile">{bookingData.client?.mobile}</Descriptions.Item>
          )}
          {showEmail && (
          <Descriptions.Item label="Email" span={2}>{bookingData.client?.email}</Descriptions.Item>
          )}
          <Descriptions.Item label="Residential Address" span={2}>
            {`${bookingData.client?.addressResidential?.streetAddress}, ${bookingData.client?.addressResidential?.suburb}, ${bookingData.client?.addressResidential?.state} ${bookingData.client?.addressResidential?.postcode}`}
          </Descriptions.Item>
          <Descriptions.Item label="Postal Address" span={2}>
            {`${bookingData.client?.addressPostal?.streetAddress}, ${bookingData.client?.addressPostal?.suburb}, ${bookingData.client?.addressPostal?.state} ${bookingData.client?.addressPostal?.postcode}`}
          </Descriptions.Item>
        </Descriptions>

        {showGpDetailsPage && (
        <Descriptions title={<span style={titleStyle}>GP Details</span>} bordered size="small" column={2}>
          <Descriptions.Item label="GP First Name">{bookingData.gp?.firstName ?? ''}</Descriptions.Item>
          <Descriptions.Item label="GP Last Name">{bookingData.gp?.lastName ?? ''}</Descriptions.Item>
          <Descriptions.Item label="Clinic Name">{bookingData.gp?.clinicName ?? ''}</Descriptions.Item>
          <Descriptions.Item label="Clinic Phone">{bookingData.gp?.clinicPhoneNumber ?? ''}</Descriptions.Item>
          {showFaxAndEmial && (
            <>
              <Descriptions.Item label="Clinic Fax">{bookingData.gp?.clinicFaxNumber ?? ''}</Descriptions.Item>
              <Descriptions.Item label="Clinic Email">{bookingData.gp?.clinicEmail ?? ''}</Descriptions.Item>
            </>
          )}
        </Descriptions>
        )}

        {showReferrerDetailsPage && (
        <Descriptions title={<span style={titleStyle}>{title}</span>} bordered size="small" column={2}>
          <Descriptions.Item label="First Name">{bookingData.referrer?.firstName}</Descriptions.Item>
          <Descriptions.Item label="Last Name">{bookingData.referrer?.lastName}</Descriptions.Item>
          <Descriptions.Item label="Phone">{bookingData.referrer?.phone}</Descriptions.Item>
          <Descriptions.Item label="Email">{bookingData.referrer?.email}</Descriptions.Item>
          {showPhn && (
            <Descriptions.Item label="PHN" span={2}>{bookingData.referrer?.referrerPHN}</Descriptions.Item>
          )}
        </Descriptions>
        )}

        <Descriptions title={<span style={titleStyle}>Health Summary</span>} bordered size="small" column={2}>
          {showAboriginalQuestion && (
          <Descriptions.Item
            label="ATSI Status"
            span={2}
          >
            {bookingData.healthSummary?.aboriginalOrTorresStraitIslander}
          </Descriptions.Item>
          )}
          {mentalHealthQuestion && (
          <Descriptions.Item
            label="Mental Health Plan"
            span={2}
          >
            {bookingData.healthSummary?.hasMentalHealthPlan}
          </Descriptions.Item>
          )}
          <Descriptions.Item
            label="Diagnosis"
            span={2}
          >
            {bookingData.healthSummary?.diagnosis ?? ''}
          </Descriptions.Item>
          {
            mmhcCentreDetails && (
            <Descriptions.Item
              label="MMHC Centre details"
              span={2}
            >
              {bookingData.healthSummary?.mmhcCentreDetails ?? ''}
            </Descriptions.Item>
            )
          }
          {
            primaryPresentingProblem && (
            <Descriptions.Item
              label="Primary Presenting problem"
              span={2}
            >
              {bookingData.healthSummary?.primaryPresentingProblem}
            </Descriptions.Item>
            )
          }
          {
            currentMedication && (
            <Descriptions.Item
              label="Current Medications"
              span={2}
            >
              {bookingData.healthSummary?.currentMedications}
            </Descriptions.Item>
            )
          }
          {
            showReasonForReferral && (
            <Descriptions.Item
              label="Reason for Referral"
              span={2}
            >
              {bookingData.healthSummary?.reasonForReferral}
            </Descriptions.Item>
            )
          }
          <Descriptions.Item
            label="Risks or Concerns"
            span={2}
          >
            {bookingData.healthSummary?.risksOrConcerns}
          </Descriptions.Item>
          <Descriptions.Item
            label="Additional Info"
            span={2}
          >
            {bookingData.healthSummary?.additionalInformation ?? ''}
          </Descriptions.Item>
        </Descriptions>

        {/* <Descriptions title={<span style={titleStyle}>Medicare Card</span>} bordered size="small" column={2}>
          <Descriptions.Item label="Card Type" span={2}>{bookingData.medicareCard?.cardType}</Descriptions.Item>
          {bookingData.medicareCard?.cardType === 'medicare' && (
            <>
              <Descriptions.Item
                label="Medicare Card Number"
              >
                {bookingData.medicareCard.medicareCardNumber}
              </Descriptions.Item>
              <Descriptions.Item
                label="Medicare Card IRN"
              >
                {bookingData.medicareCard.medicareCardIRN}
              </Descriptions.Item>
              <Descriptions.Item label="Medicare Expiry Date">{bookingData.medicareCard.expiry}</Descriptions.Item>
            </>
          )}
          {bookingData.medicareCard?.cardType === 'dva' && (
            <>
              <Descriptions.Item label="DVA Card Type">{bookingData.medicareCard.dva.cardType}</Descriptions.Item>
              <Descriptions.Item
                label="DVA Card Number"
              >
                {bookingData.medicareCard.dva.cardNumber}
              </Descriptions.Item>
            </>
          )}
        </Descriptions> */}

        {
              bookingData.attachments && (
              <Descriptions title={<span style={titleStyle}>Attachments</span>} bordered size="small" column={1}>
                {healthSummary
                        && <Descriptions.Item label="Health Summary">{healthSummary.fileName}</Descriptions.Item>}
                {medicalSupporting
                        && <Descriptions.Item label="Medical Supporting">{medicalSupporting.fileName}</Descriptions.Item>}
                {additionalReport1
                        && <Descriptions.Item label="Additional Report 1">{additionalReport1.fileName}</Descriptions.Item>}
                {additionalReport2
                        && <Descriptions.Item label="Additional Report 2">{additionalReport2.fileName}</Descriptions.Item>}
              </Descriptions>
              )
          }
      </Space>
    </div>
  );
};
